import { useRecoilState, useRecoilValue } from "recoil";
import { flightHistoryTooltipControllerState, flightHistoryWindowState } from "../../store/FlightHistoryState";
import { convertWRAPPoint, deepCopy, formatLatDM, formatLonDM, formatTimeZoneDate, formatTimeZoneStr } from "../../utils/FPEUtils";
import CloseIcon from '@material-ui/icons/Close';
import WRAP from "WRAP-JS";
import CustomDraggable from "../../parts/CustomDraggable";
import { makeStyles } from "@material-ui/core";
import { timeZoneSelector } from "../../store/TimeZoneState";
import clsx from "clsx";
import { mapContentsVisibleWithID, MAP_ID_FLIGHT_HISTORY } from "../../store/MapContentsState";
import { useRef } from "react";
import { coPiTooltipSettings } from "../../store/CopilotState";
import { useLinkableSourceList } from "../../store/AircraftSettingState";

const useStyles = makeStyles(() => ({
  toolTip: {
    padding: 5,
    backgroundColor: '#FFFFFF',
    border: 'solid 1px',
    cursor: 'move',
    position: 'absolute',
    zIndex: 100
  },
  dataHeader: {
    justifyContent: 'space-between'
  },
  dataRow: {
    display: 'flex',
    margin: 2
  },
  dataLabel: {
    width: 80,
    textAlign: 'left'
  },
  divider: {
    width: 10
  },
  closeButton: {
    cursor: 'pointer'
  }
}));

const NO_RECORD = '---';

const  FlightHistoryTooltip = () => {
  const flightHistoryVisible = useRecoilValue(mapContentsVisibleWithID(MAP_ID_FLIGHT_HISTORY));
  const [flightHistoryWindow, setFlightHistoryWindow] = useRecoilState(flightHistoryWindowState);
  const timeZone = useRecoilValue(timeZoneSelector);
  const timeZoneStr = formatTimeZoneStr(timeZone);
  const classes = useStyles();
  const flightHistoryTooltipController = useRecoilValue(flightHistoryTooltipControllerState);
  const screenPointRef = useRef({});
  const tooltipSetting = useRecoilValue(coPiTooltipSettings);
  const {linkableSourceList} = useLinkableSourceList();

  const hidden = !flightHistoryVisible;
  const parentContainer = document.getElementById('map-container');

  const closeButtonClickHandler = geo => {
    delete screenPointRef.current[`${geo.properties.obs_time}:${geo.properties.data_source_id}`];
    setFlightHistoryWindow(prev => {
      const newData = deepCopy(prev);
      const idx = newData.findIndex(d => d.properties.obs_time === geo.properties.obs_time && d.properties.data_source_id === geo.properties.data_source_id);
      newData.splice(idx, 1);
      return newData;
    })
  }

  return (
    <>
      {
        flightHistoryWindow.map((geo) => {
          let name = '';
          if (flightHistoryTooltipController.find(v => v.name === 'Aircraft Code').visible) {
            name += `${geo.properties.aircraft_code} `;
          }
          if (flightHistoryTooltipController.find(v => v.name === 'Aircraft Name').visible) {
            name += `(${geo.properties.aircraft_name})`;
          }

          let sPoint;
          const key = `${geo.properties.obs_time}:${geo.properties.data_source_id}`;
          if (screenPointRef.current[key]) {
            sPoint = screenPointRef.current[key];
          } else {
            sPoint = WRAP.Geo.getScreenPoint(convertWRAPPoint(geo.geometry.coordinates[1], geo.geometry.coordinates[0]));
            screenPointRef.current[key] = sPoint;
          }

          const tooltipData = {
            "%conv_obs_time%": geo.properties.obs_time ? `${formatTimeZoneDate(geo.properties.obs_time, timeZone, 'YYYY/MM/DD HH:mm')} ${timeZoneStr}`: NO_RECORD,
            "%conv_alt_ft%" : typeof geo.properties.alt_ft === "number" ? Math.round(geo.properties.alt_ft * 100) / 100 : NO_RECORD,
            "%conv_lat%" : geo.geometry.coordinates[1] ? formatLatDM(geo.geometry.coordinates[1]) : NO_RECORD,
            "%conv_lon%" : geo.geometry.coordinates[0] ? formatLonDM(geo.geometry.coordinates[0]) : NO_RECORD,
            "%conv_source%": geo.properties.data_source_id ? linkableSourceList.find(l => l.data_source_id === geo.properties.data_source_id)?.data_source_name : NO_RECORD
          }

          const filteredTooltipSetting = tooltipSetting[geo.properties.data_source_id]?.["History-tooltip"].filter(t => {
            return flightHistoryTooltipController.find(o => o.name === t.label).visible;
          });

          const toolTips = filteredTooltipSetting?.map(tooltip => {
            const keys = tooltip.value.match(/%\w+%/g);
            let value = tooltip.value;
            keys.forEach(key => {
              const convValue = tooltipData[key];
              if (convValue !== undefined) {
                value = value.replace(key, convValue);
              } else {
                const rowValue = geo.properties[key.replace(/%/g, "")];
                value = value.replace(key, rowValue !== null && rowValue !== undefined ? rowValue : NO_RECORD);
              }
            });
            return {...tooltip, ...{value: value}};
          });

          return (
            <CustomDraggable
              baseX={sPoint.x}
              baseY={sPoint.y}
              parent={parentContainer}
              key={`flightHistoryTooltip-${geo.properties.data_source_id}-${geo.properties.obs_time}`}
            >
              <div className={classes.toolTip} style={{top: `${sPoint.y}px`,left: `${sPoint.x}px`}} hidden={hidden}>
                <div className={clsx(classes.dataHeader, classes.dataRow)}>
                  <div>{name}</div>
                  <div className={classes.closeButton} onClick={() => closeButtonClickHandler(geo)}><CloseIcon fontSize="small"></CloseIcon></div>
                </div>
                {
                  toolTips?.map(tooltip => {
                    return <div className={classes.dataRow} key={tooltip.label}>
                      <div className={classes.dataLabel}>{tooltip.label}</div>
                      <div className={classes.divider}>:</div>
                      <div>{tooltip.value}</div>
                    </div>
                  })
                }
              </div>
            </CustomDraggable>
          )
        })
      }
    </>
  )
}

export default FlightHistoryTooltip;